import { useState, useCallback } from 'react';
import { X } from 'lucide-react';
import { supabase } from '@/config/supabase';
import { useAuth } from '@/context';
import { useToast } from '@/components/ui/Toast';
import type { FeedbackModalProps } from './FeedbackModal.types';
import styles from './FeedbackModal.module.css';

const CATEGORIES = [
  { value: 'general', label: 'General Feedback' },
  { value: 'bug', label: 'Bug Report' },
  { value: 'feature', label: 'Feature Request' },
  { value: 'design', label: 'Design Quality' },
  { value: 'other', label: 'Other' },
];

export function FeedbackModal({ isOpen, onClose }: FeedbackModalProps) {
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();
  const [email, setEmail] = useState(user?.email ?? '');
  const [category, setCategory] = useState('general');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!message.trim()) return;

      setSubmitting(true);
      try {
        const { error } = await supabase.from('feedback').insert({
          user_id: user?.id ?? null,
          email: email.trim() || null,
          category,
          message: message.trim(),
          page_url: window.location.href,
          user_agent: navigator.userAgent,
        });

        if (error) throw error;

        showSuccess('Thank you for your feedback!');
        setMessage('');
        setCategory('general');
        onClose();
      } catch (err) {
        console.error('[FeedbackModal] Submit failed:', err);
        showError('Failed to submit feedback. Please try again.');
      } finally {
        setSubmitting(false);
      }
    },
    [user, email, category, message, onClose, showSuccess, showError]
  );

  if (!isOpen) return null;

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h2 className={styles.title}>Send Feedback</h2>
          <button type="button" className={styles.closeButton} onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <form className={styles.form} onSubmit={handleSubmit}>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="feedback-email">
              Email (optional)
            </label>
            <input
              id="feedback-email"
              type="email"
              className={styles.input}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
            />
          </div>

          <div className={styles.field}>
            <label className={styles.label} htmlFor="feedback-category">
              Category
            </label>
            <select
              id="feedback-category"
              className={styles.select}
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              {CATEGORIES.map((cat) => (
                <option key={cat.value} value={cat.value}>
                  {cat.label}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.field}>
            <label className={styles.label} htmlFor="feedback-message">
              Message
            </label>
            <textarea
              id="feedback-message"
              className={styles.textarea}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Tell us what you think..."
              required
            />
          </div>

          <div className={styles.actions}>
            <button type="button" className={styles.cancelButton} onClick={onClose}>
              Cancel
            </button>
            <button
              type="submit"
              className={styles.submitButton}
              disabled={submitting || !message.trim()}
            >
              {submitting ? 'Sending...' : 'Send Feedback'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
